import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import ProtectedRoute from './components/ProtectedRoute';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import AppShell from './components/AppShell';

import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AccessDeniedPage from './pages/AccessDeniedPage';
import DashboardPage from './pages/DashboardPage';
import MyCandidatesPage from './pages/MyCandidatesPage';
import CandidatesListPage from './pages/CandidatesListPage';
import CandidateFormPage from './pages/CandidateFormPage';
import CandidateViewPage from './pages/CandidateViewPage';
import SearchPage from './pages/SearchPage';
import ApiTokensPage from './pages/ApiTokensPage';
import ApiTokenCreatePage from './pages/ApiTokenCreatePage';
import SettingsPage from './pages/SettingsPage';
import StatesListPage from './pages/StatesListPage';
import StateFormPage from './pages/StateFormPage';
import CountiesListPage from './pages/CountiesListPage';
import CountyFormPage from './pages/CountyFormPage';
import CountyChildListPage from './pages/CountyChildListPage';
import CountyChildFormPage from './pages/CountyChildFormPage';
import DataManagementPage from './pages/DataManagementPage';
import DataSourcesListPage from './pages/DataSourcesListPage';
import DataSourceFormPage from './pages/DataSourceFormPage';
import ImportJobsListPage from './pages/ImportJobsListPage';
import ImportJobDetailPage from './pages/ImportJobDetailPage';
import ZipCodesPage from './pages/ZipCodesPage';
import UsersListPage from './pages/UsersListPage';
import UserFormPage from './pages/UserFormPage';
import ElectionsListPage from './pages/ElectionsListPage';
import ElectionFormPage from './pages/ElectionFormPage';
import ElectionDetailPage from './pages/ElectionDetailPage';
import PrimaryReconciliationPage from './pages/PrimaryReconciliationPage';
import SubscribePage from './pages/SubscribePage';
import SubscribeSuccessPage from './pages/SubscribeSuccessPage';
import BillingAdminPage from './pages/BillingAdminPage';
import MySubscriptionPage from './pages/MySubscriptionPage';
import JurisdictionsPage from './pages/JurisdictionsPage';
import OfficesPage from './pages/OfficesPage';
import StagingInboxPage from './pages/StagingInboxPage';
import ElectionDatesPage from './pages/ElectionDatesPage';

function Shell({ children }) {
  return (
    <ProtectedRoute>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

function AdminShell({ children }) {
  return (
    <RoleProtectedRoute allow={['admin']}>
      <AppShell>{children}</AppShell>
    </RoleProtectedRoute>
  );
}

/**
 * Role-aware dashboard route: admins land on the analytics dashboard,
 * campaign managers land on their assigned-candidates view.
 */
function DashboardRoute() {
  const { user } = useAuth();
  if (user?.role === 'campaign_manager') {
    return <Navigate to="/my-candidates" replace />;
  }
  return <DashboardPage />;
}

const COUNTY_SECTIONS = ['election-dates', 'supervisors', 'party-organizations', 'local-groups'];

function App() {
  return (
    <div className="App">
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/subscribe" element={<SubscribePage />} />
              <Route path="/subscribe/success" element={<SubscribeSuccessPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/access-denied" element={<AccessDeniedPage />} />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Shell><DashboardRoute /></Shell>} />
              <Route path="/my-candidates" element={<Shell><MyCandidatesPage /></Shell>} />
              <Route path="/my-subscription" element={<Shell><MySubscriptionPage /></Shell>} />
              <Route path="/candidates" element={<AdminShell><CandidatesListPage /></AdminShell>} />
              <Route path="/candidates/create" element={<AdminShell><CandidateFormPage mode="create" /></AdminShell>} />
              <Route path="/candidates/:id" element={<Shell><CandidateViewPage /></Shell>} />
              <Route path="/candidates/:id/edit" element={<Shell><CandidateFormPage mode="edit" /></Shell>} />
              <Route path="/search" element={<AdminShell><SearchPage /></AdminShell>} />
              <Route path="/tokens" element={<AdminShell><ApiTokensPage /></AdminShell>} />
              <Route path="/tokens/create" element={<AdminShell><ApiTokenCreatePage /></AdminShell>} />
              <Route path="/api-tokens" element={<Navigate to="/tokens" replace />} />
              <Route path="/api-tokens/create" element={<Navigate to="/tokens/create" replace />} />
              <Route path="/settings" element={<AdminShell><SettingsPage /></AdminShell>} />
              <Route path="/admin/users" element={<AdminShell><UsersListPage /></AdminShell>} />
              <Route path="/admin/users/new" element={<AdminShell><UserFormPage mode="create" /></AdminShell>} />
              <Route path="/admin/users/:id" element={<AdminShell><UserFormPage mode="edit" /></AdminShell>} />
              <Route path="/admin/elections" element={<AdminShell><ElectionsListPage /></AdminShell>} />
              <Route path="/admin/elections/new" element={<AdminShell><ElectionFormPage mode="create" /></AdminShell>} />
              <Route path="/admin/elections/:id" element={<AdminShell><ElectionDetailPage /></AdminShell>} />
              <Route path="/admin/elections/:id/reconcile" element={<AdminShell><PrimaryReconciliationPage /></AdminShell>} />
              <Route path="/admin/elections/:id/edit" element={<AdminShell><ElectionFormPage mode="edit" /></AdminShell>} />
              <Route path="/admin/calendar" element={<Navigate to="/admin/elections" replace />} />
              <Route path="/admin/jurisdictions" element={<AdminShell><JurisdictionsPage /></AdminShell>} />
              <Route path="/admin/offices" element={<AdminShell><OfficesPage /></AdminShell>} />
              <Route path="/admin/staging-inbox" element={<AdminShell><StagingInboxPage /></AdminShell>} />
              <Route path="/admin/billing" element={<AdminShell><BillingAdminPage /></AdminShell>} />
              <Route path="/admin/election-dates" element={<AdminShell><ElectionDatesPage /></AdminShell>} />
              <Route path="/admin/states" element={<AdminShell><StatesListPage /></AdminShell>} />
              <Route path="/admin/states/new" element={<AdminShell><StateFormPage mode="create" /></AdminShell>} />
              <Route path="/admin/states/:stateId" element={<AdminShell><StateFormPage mode="edit" /></AdminShell>} />
              <Route path="/admin/states/:stateId/data-management" element={<AdminShell><DataManagementPage /></AdminShell>} />
              <Route path="/admin/states/:stateId/data-sources" element={<AdminShell><DataSourcesListPage /></AdminShell>} />
              <Route path="/admin/states/:stateId/data-sources/new" element={<AdminShell><DataSourceFormPage mode="create" /></AdminShell>} />
              <Route path="/admin/states/:stateId/data-sources/:sourceId" element={<AdminShell><DataSourceFormPage mode="edit" /></AdminShell>} />
              <Route path="/admin/states/:stateId/import-jobs" element={<AdminShell><ImportJobsListPage /></AdminShell>} />
              <Route path="/admin/states/:stateId/import-jobs/:jobId" element={<AdminShell><ImportJobDetailPage /></AdminShell>} />
              <Route path="/admin/zip-codes" element={<AdminShell><ZipCodesPage /></AdminShell>} />
              <Route path="/admin/states/:stateId/counties" element={<AdminShell><CountiesListPage /></AdminShell>} />
              <Route path="/admin/states/:stateId/counties/new" element={<AdminShell><CountyFormPage mode="create" /></AdminShell>} />
              <Route path="/admin/states/:stateId/counties/:countyId" element={<AdminShell><CountyFormPage mode="edit" /></AdminShell>} />
              {COUNTY_SECTIONS.flatMap((s) => [
                <Route key={`${s}-list`} path={`/admin/states/:stateId/counties/:countyId/${s}`} element={<AdminShell><CountyChildListPage section={s} /></AdminShell>} />,
                <Route key={`${s}-new`} path={`/admin/states/:stateId/counties/:countyId/${s}/new`} element={<AdminShell><CountyChildFormPage section={s} mode="create" /></AdminShell>} />,
                <Route key={`${s}-edit`} path={`/admin/states/:stateId/counties/:countyId/${s}/:itemId`} element={<AdminShell><CountyChildFormPage section={s} mode="edit" /></AdminShell>} />,
              ])}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </div>
  );
}

export default App;
